import {Component, Event, EventEmitter, h, Prop, State} from '@stencil/core';
import {StarchApplication} from "../../interfaces/starch-application";
import {City} from "../../interfaces/city";
import {TicketForm} from "../../interfaces/ticket-form";
import {TicketType} from "../../interfaces/ticket-type";
import {StarchTicketService} from "../../services/starch-ticket.service";
import {StarchTicket} from "../../interfaces/starch-ticket";
import {StarchTicketCreateResponse} from "../../interfaces/starch-ticket-create-response";
import * as yup from 'yup';
import {ValidationError} from "../../interfaces/validation-error";

declare var window: { CodCat: string };

@Component({
    tag: 'starch-base-ticket-form',
    styleUrl: './starch-base-ticket-form.css',
    shadow: false
})
export class StarchBaseTicketForm {

    @Prop() email: string;
    @Prop() name: string;
    @Prop() userType: string;
    @Prop() codCat: string;
    @Prop() city: string;
    @Prop() phone: string;
    @Prop() application: string;
    @Prop() showMessagePages: boolean = true;
    @Prop() layout: 'starch' | 'unstyled' | 'standard';

    @Event() ticketSent: EventEmitter<StarchTicketCreateResponse>;
    @Event() ticketError: EventEmitter<string>;
    @State() cities: City[];

    @State() applications: StarchApplication[];
    @State() ticketTypes: TicketType[];
    @State() validationErrors: {
        [key: string]: string
    };
    @State() ticketForm: TicketForm;
    @State() isTicketSent: boolean;
    @State() isTicketError: boolean;
    @State() ticketFormVersion: number;
    @State() ticketFormValid: boolean;
    @State() loading: boolean;
    @State() showErrors: boolean;

    starchTicketService: StarchTicketService;
    validationSchema: any;

    constructor() {

        this.starchTicketService = new StarchTicketService();

        this.cities = [];
        this.applications = [];
        this.ticketTypes = [];
        this.validationErrors = {};
        this.showErrors = false;

        this.isTicketSent = false;
        this.ticketFormVersion = 0;
        this.ticketFormValid = false;
        this.loading = false;

        this.init().catch();
        if (!this.codCat) {
            this.codCat = window.CodCat;
        }
        if (!this.codCat) {
            let body: HTMLBodyElement = document.querySelector('body');
            this.codCat = body.dataset.comune;
        }
    }

    findApplication(name: string) {
        const appIndex = this.applications.findIndex((application: StarchApplication) => {
            return name.toLowerCase() === application.labels.it.toLowerCase()
        });
        return appIndex !== -1 ? this.applications[appIndex].labels.it : '';
    }

    async searchCity(name: string) {
        if (name.length >= 3) {
            this.cities = await this.starchTicketService.getCities(name);
        } else {
            this.cities = [];
            this.setCity();
        }
    }

    async init() {
        const config = await this.starchTicketService.getConfig();
        this.starchTicketService.accessToken = 'Bearer ' + config.data.access_token;
        this.starchTicketService.clientID = config.data.deskero_client;

        this.cities = [];
        this.applications = await this.starchTicketService.getApplications();
        this.ticketTypes = await this.starchTicketService.getErrorTypes();

        this.ticketForm = {
            name: this.name ? this.name : '',
            email: this.email ? this.email : '',
            phone: this.phone ? this.phone : '',
            userType: this.userType ? this.userType : '0',
            city: this.city ? this.city.toUpperCase() : '',
            application: this.application ? this.findApplication(this.application) : '',
            error: '',
            ticketSubject: '',
            ticketDescription: '',
            ticketPriority: '0',
            attachedFileName: '',
            attachedFileType: '',
            attachedFileBase64: '',
            privacyAccepted: false
        };

        this.city = this.city ? this.city.toUpperCase() : undefined;
        this.buildValidationSchema();
        this.checkFormValidity();
    }


    /**
     * Build validation schema
     * @private
     */
    private buildValidationSchema() {
        this.validationSchema = yup.object().shape({
            name: yup.string().required('Il nominativo è richiesto'),
            email: yup.string().required('L\'indirizzo e-mail è richiesto').email('Inserire un indirizzo e-mail valido'),
            phone: yup.string().required('Il telefono è richiesto'),
            userType: yup.string().oneOf(['0', '1']).required('Specificare il tipo di utente'),
            city: yup.string().required('Il comune è richiesto'),
            application: yup.string().required('Selezionare l\'applicativo'),
            error: yup.string().required('Selezionare un tipo di segnalazione'),
            ticketSubject: yup.string().required('L\'oggetto è richiesto'),
            ticketDescription: yup.string().required('La descrizione è richiesta'),
            privacyAccepted: yup.bool().oneOf([true], 'Seleziona il consenso al trattamento dei dati personali'),
        });
    }

    /**
     * Form field changed
     * @param event Change event with value
     * @param field TicketForm field name
     */
    formValueChanged(event, field: string) {
        if (this.ticketForm.hasOwnProperty(field)) {
            this.ticketForm[field] = event.target.value;
            if (field === 'privacyAccepted' || field === 'ticketHighPriority') {
                this.ticketForm[field] = event.target.value === '1';
            }
            if (field === 'userType' && event.target.value === '1') {
                this.ticketForm.application = 'CPortal';
            }
            this.ticketFormVersion++;
            this.checkFormValidity();
        }
    }

    /**
     * City autocomplete
     * @param event
     */
    async formSearchCityChanged(event) {
        await this.searchCity(event.target.value);
    }

    setCity(city?: City) {
        if (city) {
            this.ticketForm.city = city.company;
            this.cities = [];
        } else {
            this.ticketForm.city = undefined;
        }
        this.checkFormValidity();
    }

    /**
     * Form file changed
     * Form file changed
     * @param event
     */
    fileChanged(event) {
        let reader = new FileReader();
        if (event.target.files && event.target.files.length > 0) {
            let file = event.target.files[0];
            reader.readAsDataURL(file);
            this.ticketForm.attachedFileName = file.name;
            this.ticketForm.attachedFileType = file.type;
            reader.onload = () => {
                this.ticketForm.attachedFileBase64 = reader.result;
                this.ticketFormVersion++;
                this.checkFormValidity();
            };
        }
    }

    async sendTicket(e) {
        e.preventDefault();
        this.showErrors = true;

        this.checkFormValidity().then(async () => {
            this.loading = true;
            const resp = await this.starchTicketService.checkUserMail(this.ticketForm.email);
            if (resp) {
                await this.insertTicket(resp.customer.records[0].id);
            } else {
                try {
                    const newUser = await this.starchTicketService.createDeskeroUser({
                        name: this.ticketForm.name,
                        email: this.ticketForm.email,
                        language: 'it',
                        company: this.ticketForm.userType === '0' ? this.ticketForm.city : ''
                    });
                    if (newUser) {
                        await this.insertTicket(newUser.id);
                    }
                } catch (e) {
                    this.isTicketError = true;
                    this.loading = false;
                    this.ticketError.emit(e);
                }
            }
        });
    }

    async insertTicket(userId: string) {
        const res = await this.starchTicketService.getStarchApplicationId(this.ticketForm.application);
        if (res && res.group) {
            let ticketToAdd: StarchTicket = {
                subject: this.ticketForm.ticketSubject,
                description: this.ticketForm.ticketDescription,
                priority: this.ticketForm.ticketPriority === '1' ? 1 : 0,
                openedBy: {
                    id: userId
                },
                type: {
                    id: this.ticketForm.error
                },
                group: {
                    id: res.group.records[0].id
                },
                customFields: {
                    tipo_utente:
                        this.ticketForm.userType === '0'
                            ? 'Tecnico Comunale'
                            : 'Professionista\\Cittadino',
                    Telefono: this.ticketForm.phone,
                    per_il_comune_di:
                        this.ticketForm.userType === '1'
                            ? this.ticketForm.city
                            : ''
                }
            };

            if (this.ticketForm.attachedFileName) {
                ticketToAdd.attachedDocuments = [
                    {
                        documentName: this.ticketForm.attachedFileName,
                        documentType: this.ticketForm.attachedFileType,
                        documentBase64: this.ticketForm.attachedFileBase64.toString().split(',')[1]
                    }
                ]
            }
            try {
                const createdTicket = await this.starchTicketService.createTicket(ticketToAdd);
                const details = await this.starchTicketService.getTicketDetails(createdTicket.id);
                this.isTicketSent = true;
                this.loading = false;
                this.ticketSent.emit(details);
            } catch (e) {
                this.isTicketError = true;
                this.loading = false;
                this.ticketError.emit(e);
            }
        } else {
            this.loading = false;
            this.ticketError.emit('Impossibile trovare l\'application ID');
        }
    }

    calcColumnSize(size: number) {
        return size;
    }

    /**
     * Check validation schema
     */
    checkFormValidity(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.validationSchema.validate(this.ticketForm, {abortEarly: false})
                .then(() => {
                    this.errorsToObject([]);
                    resolve(true);
                })
                .catch((e) => {
                    this.errorsToObject(e.inner);
                    reject(`Controllare i campi necessari`);
                });
        });
    }

    /**
     * Convert validationError to field => errorText
     *
     * @param validationErrors
     */
    errorsToObject(validationErrors: ValidationError[]) {
        this.validationErrors = {};
        for (let error of validationErrors) {
            this.validationErrors[error.path] = error.message;
        }
    }

    standardLayout() {
        if (this.ticketForm && (!this.isTicketSent && !this.isTicketError || !this.showMessagePages)) {
            return (
                <form class="starch-tickets-app" autoComplete="off" onSubmit={(e) => this.sendTicket(e)}>
                    <h3>Segnalazione inviata da</h3>
                    <div class="form-group">
                        <label htmlFor="name">Nominativo</label>
                        <input type="text" class={`form-control ${this.validationErrors.name && 'invalid-field'}`}
                               placeholder="Nome Cognome" name="name"
                               value={this.ticketForm.name}
                               onBlur={this.checkFormValidity.bind(this)}
                               onInput={(event) => this.formValueChanged(event, 'name')}/>
                        {this.validationErrors.name && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.name}</small>}
                    </div>
                    <div class="form-group">
                        <label htmlFor="email">E-mail</label>
                        <input type="text" pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                               class={`form-control ${this.validationErrors.email && 'invalid-field'}`}
                               name="email"
                               value={this.ticketForm.email}
                               onBlur={this.checkFormValidity.bind(this)}
                               onInput={(event) => this.formValueChanged(event, 'email')}/>
                        {this.validationErrors.email && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.email}</small>}
                    </div>
                    <div class="form-group">
                        <label htmlFor="phone">Telefono</label>
                        <input type="tel" class={`form-control ${this.validationErrors.phone && 'invalid-field'}`}
                               id="phone"
                               name="phone"
                               value={this.ticketForm.phone}
                               onBlur={this.checkFormValidity.bind(this)}
                               onInput={(event) => this.formValueChanged(event, 'phone')}/>
                        {this.validationErrors.phone && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.phone}</small>}
                    </div>
                    {
                        this.userType == undefined
                            ?
                            <div class="form-group">
                                <label htmlFor="userType">Tipo utente</label>
                                <div>
                                    <div class="form-check">
                                        <input class="form-check-input" type="radio" name="userType"
                                               id="tecnicoComunale" value="0"

                                               checked={this.ticketForm.userType == '0'}
                                               onInput={(event) => this.formValueChanged(event, 'userType')}/>
                                        <label class="form-check-label">Tecnico comunale</label>
                                    </div>
                                    <div class="form-check">
                                        <input class="form-check-input" type="radio" name="userType"
                                               id="professionistaCittadino"
                                               value="1"
                                               checked={this.ticketForm.userType == '1'}
                                               onInput={(event) => this.formValueChanged(event, 'userType')}/>
                                        <label class="form-check-label">Professionista / cittadino</label>
                                    </div>
                                </div>
                                {this.validationErrors.userType && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.userType}</small>}
                            </div>
                            : ''
                    }
                    {
                        this.city == undefined
                            ?
                            <div class="form-group">
                                <label
                                    htmlFor="city">{this.ticketForm.userType == '1' ? 'Per il comune di' : 'Comune'}</label>
                                <input type="text"
                                       class={`form-control ${this.validationErrors.city && 'invalid-field'}`}
                                       placeholder="Cerca un comune" name="city"
                                       value={this.ticketForm.city}
                                       onInput={(event) => this.formSearchCityChanged(event)}/>
                                {this.cities && this.cities.length > 0 &&
                                    <ul class="city-search-results">
                                        {this.cities.map(city => (
                                            <li>
                                                <a pull-left
                                                   onClick={() => this.setCity(city)}>{city.city ? city.city.toUpperCase() : city.company.toUpperCase()}</a>
                                            </li>)
                                        )}
                                    </ul>
                                }
                                {this.validationErrors.city && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.city}</small>}
                            </div>
                            : ''
                    }

                    <h3>Dettagli segnalazione</h3>
                    {
                        this.application !== undefined || this.ticketForm.userType == '1'
                            ? ''
                            :
                            <div class="form-group">
                                <label htmlFor="application">Applicativo</label>
                                <select id="application"
                                        class={`form-control ${this.validationErrors.application && 'invalid-field'}`}
                                        name="application"
                                        onChange={(event) => this.formValueChanged(event, 'application')}>
                                    <option value='' selected={this.ticketForm.application === ''}>Seleziona</option>
                                    {this.applications.map(application => (
                                        <option value={application.labels.it}
                                                selected={this.ticketForm.application.toLowerCase() === application.labels.it.toLowerCase()}>{application.labels.it}</option>)
                                    )}
                                </select>
                                {this.validationErrors.application && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.application}</small>}
                            </div>
                    }

                    <div class="form-group">
                        <label htmlFor="error">Segnalazione</label>
                        <select id="error" class={`form-control ${this.validationErrors.error && 'invalid-field'}`}
                                name="error"
                                onChange={(event) => this.formValueChanged(event, 'error')}>
                            <option value='' selected={this.ticketForm.error === ''}>Seleziona</option>
                            {this.ticketTypes.map(ticketType => (
                                <option value={ticketType.id}
                                        selected={this.ticketForm.error === ticketType.id}>{ticketType.labels.it}</option>)
                            )}
                        </select>
                        {this.validationErrors.error && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.error}</small>}
                    </div>

                    <div class="form-group">
                        <label htmlFor="ticketSubject">Oggetto</label>
                        <input type="text"
                               class={`form-control ${this.validationErrors.ticketSubject && 'invalid-field'}`}
                               id="ticketSubject" placeholder="Oggetto della segnalazione"
                               name="ticketSubject" value={this.ticketForm.ticketSubject}
                               onInput={(event) => this.formValueChanged(event, 'ticketSubject')}/>
                        {this.validationErrors.ticketSubject && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.ticketSubject}</small>}
                    </div>

                    <div class="form-group">
                        <label htmlFor="ticketDescription">Descrizione</label>
                        <textarea class={`form-control ${this.validationErrors.ticketDescription && 'invalid-field'}`}
                                  id="ticketDescription" placeholder="Descrizione dettagliata..."
                                  name="ticketDescription" value={this.ticketForm.ticketDescription} rows={10}
                                  onInput={(event) => this.formValueChanged(event, 'ticketDescription')}/>
                        {this.validationErrors.ticketDescription && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.ticketDescription}</small>}
                    </div>

                    <div class="form-group">
                        <label htmlFor="ticketPriority">Priorità</label>
                        <select id="ticketPriority" class="form-control" name="ticketPriority"
                                onChange={(event) => this.formValueChanged(event, 'ticketPriority')}>
                            <option value='0' selected={this.ticketForm.error === '0'}>Media</option>
                            <option value='1' selected={this.ticketForm.error === '1'}>Alta</option>
                        </select>
                        {this.validationErrors.ticketPriority && this.showErrors &&
                            <small class="form-text text-danger">{this.validationErrors.ticketPriority}</small>}
                    </div>

                    <div class="form-group">
                        <label>Allega un file</label>
                        <div class="custom-file">
                            <input type="file" class="custom-file-input" onInput={(e) => this.fileChanged(e)}
                                   id="allegato"
                                   name="allegato"/>
                            <label class="custom-file-label">{
                                this.ticketForm.attachedFileName == '' ?
                                    <span>Scegli il file...</span> :
                                    this.ticketForm.attachedFileName}</label>
                        </div>
                    </div>

                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="privacy" name="privacy" value="1"
                               checked={this.ticketForm.privacyAccepted}
                               onChange={(event) => this.formValueChanged(event, 'privacyAccepted')}/>
                        <label class="form-check-label">Il sottoscritto dichiara di aver letto preventivamente
                            l'informativa e
                            acconsente ai sensi e per
                            gli effetti dell'art. 13 del Regolamento Europeo 679/2016 (GDPR) al trattamento dei dati
                            personali
                            secondo le
                            modalità e nei limiti di cui <a
                                href="http://www.starch.it/Portals/0/File/InformativaPrivacyStarch.pdf?ver=2019-02-15-172110-120"
                                target="_blank">all’informativa allegata</a>.</label>
                    </div>
                    {this.validationErrors.privacyAccepted && this.showErrors &&
                        <small class="form-text text-danger">{this.validationErrors.privacyAccepted}</small>}
                    <div class="mt-3 text-right">
                        <button class="btn btn-primary" type="submit" disabled={this.loading}>Invia segnalazione
                        </button>
                    </div>
                </form>
            );
        }
        if (this.isTicketSent && this.showMessagePages) {
            return (
                <div class="text-center h-100">
                    <div class="mt-5">
                        <img src="http://www.starch.it/Portals/0/Logo_STARCH_2016_O.png?ver=2018-10-30-215215-000"
                             width="200"
                             height="42"
                             alt="" class="mb-5"/>
                        <h2 class="mb-2">Grazie per averci inviato la tua segnalazione.</h2>
                        <h4>I nostri tecnici la prenderanno in carico al più presto e ti contatteranno.</h4>
                    </div>
                </div>
            )
        }
        if (this.isTicketError && this.showMessagePages) {
            return (
                <div class="text-center h-100">
                    <div class="mt-5">
                        <img src="http://www.starch.it/Portals/0/Logo_STARCH_2016_O.png?ver=2018-10-30-215215-000"
                             width="200"
                             height="42"
                             alt="" class="mb-5"/>
                        <h2 class="mb-2">Uh oh... Qualcosa è andato storto.</h2>
                        <h4><a onClick={() => this.isTicketError = false}>Riprova ad inserire la tua segnalazione</a>, o
                            contattaci
                            direttamente al numero <b>039.62.860</b>.</h4>
                    </div>
                </div>
            );
        }
    }

    starchLayout() {
        if (this.ticketForm && (!this.isTicketSent && !this.isTicketError || !this.showMessagePages)) {
            return (
                <form class="starch-tickets-app" autoComplete="off" onSubmit={(e) => this.sendTicket(e)}>
                    <div class="row">
                        <div class="col-lg-6">
                            <h3>Segnalazione inviata da</h3>

                            <div class="form-group">
                                <label htmlFor="name">Nominativo</label>
                                <input type="text"
                                       class={`form-control ${this.validationErrors.name && 'invalid-field'}`}
                                       placeholder="Nome Cognome" name="name"
                                       value={this.ticketForm.name}
                                       onBlur={this.checkFormValidity.bind(this)}
                                       onInput={(event) => this.formValueChanged(event, 'name')}/>
                                {this.validationErrors.name && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.name}</small>}
                            </div>

                            {
                                this.city == undefined
                                    ?
                                    <div class="form-group">
                                        <label
                                            htmlFor="city">{this.ticketForm.userType == '1' ? 'Per il comune di' : 'Comune'}</label>
                                        <input type="text"
                                               class={`form-control ${this.validationErrors.city && 'invalid-field'}`}
                                               placeholder="Cerca un comune" name="city"
                                               value={this.ticketForm.city}
                                               onInput={(event) => this.formSearchCityChanged(event)}/>
                                        {this.cities && this.cities.length > 0 &&
                                            <ul class="city-search-results">
                                                {this.cities.map(city => (
                                                    <li>
                                                        <a pull-left href="javascript://"
                                                           onClick={() => this.setCity(city)}>{city.city ? city.city.toUpperCase() : city.company.toUpperCase()}</a>
                                                    </li>)
                                                )}
                                            </ul>
                                        }
                                        {this.validationErrors.city && this.showErrors &&
                                            <small class="form-text text-danger">{this.validationErrors.city}</small>}
                                    </div>
                                    : ''
                            }

                            <div class="form-group">
                                <label htmlFor="email">E-mail</label>
                                <input type="text" pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                                       class={`form-control ${this.validationErrors.email && 'invalid-field'}`}
                                       name="email"
                                       value={this.ticketForm.email}
                                       onBlur={this.checkFormValidity.bind(this)}
                                       onInput={(event) => this.formValueChanged(event, 'email')}/>
                                {this.validationErrors.email && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.email}</small>}
                            </div>

                            <div class="form-group">
                                <label htmlFor="phone">Telefono</label>
                                <input type="tel"
                                       class={`form-control ${this.validationErrors.phone && 'invalid-field'}`}
                                       id="phone"
                                       name="phone"
                                       value={this.ticketForm.phone}
                                       onBlur={this.checkFormValidity.bind(this)}
                                       onInput={(event) => this.formValueChanged(event, 'phone')}/>
                                {this.validationErrors.phone && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.phone}</small>}
                            </div>

                            {
                                this.userType == undefined
                                    ?
                                    <div class="form-group">
                                        <label htmlFor="userType">Tipo utente</label>
                                        <div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="userType"
                                                       id="tecnicoComunale" value="0"

                                                       checked={this.ticketForm.userType == '0'}
                                                       onInput={(event) => this.formValueChanged(event, 'userType')}/>
                                                <label class="form-check-label">Tecnico comunale</label>
                                            </div>
                                            <div class="form-check">
                                                <input class="form-check-input" type="radio" name="userType"
                                                       id="professionistaCittadino"
                                                       value="1"
                                                       checked={this.ticketForm.userType == '1'}
                                                       onInput={(event) => this.formValueChanged(event, 'userType')}/>
                                                <label class="form-check-label">Professionista / cittadino</label>
                                            </div>
                                        </div>
                                        {this.validationErrors.userType && this.showErrors &&
                                            <small
                                                class="form-text text-danger">{this.validationErrors.userType}</small>}
                                    </div>
                                    : ''
                            }

                        </div>
                        <div class="col-lg-6">
                            <h3>Dettagli segnalazione</h3>
                            <div class="row">
                                {
                                    this.application !== undefined || this.ticketForm.userType == '1'
                                        ? ''
                                        :
                                        <div class="col">
                                            <div class="form-group">
                                                <label htmlFor="application">Applicativo</label>
                                                <select id="application"
                                                        class={`form-control ${this.validationErrors.application && 'invalid-field'}`}
                                                        name="application"
                                                        onChange={(event) => this.formValueChanged(event, 'application')}>
                                                    <option value=''
                                                            selected={this.ticketForm.application === ''}>Seleziona
                                                    </option>
                                                    {this.applications.map(application => (
                                                        <option value={application.labels.it}
                                                                selected={this.ticketForm.application.toLowerCase() === application.labels.it.toLowerCase()}>{application.labels.it}</option>)
                                                    )}
                                                </select>
                                                {this.validationErrors.application && this.showErrors &&
                                                    <small
                                                        class="form-text text-danger">{this.validationErrors.application}</small>}
                                            </div>
                                        </div>
                                }
                                <div class="col">
                                    <div class="form-group">
                                        <label htmlFor="error">Segnalazione</label>
                                        <select id="error"
                                                class={`form-control ${this.validationErrors.error && 'invalid-field'}`}
                                                name="error"
                                                onChange={(event) => this.formValueChanged(event, 'error')}>
                                            <option value='' selected={this.ticketForm.error === ''}>Seleziona</option>
                                            {this.ticketTypes.map(ticketType => (
                                                <option value={ticketType.id}
                                                        selected={this.ticketForm.error === ticketType.id}>{ticketType.labels.it}</option>)
                                            )}
                                        </select>
                                        {this.validationErrors.error && this.showErrors &&
                                            <small class="form-text text-danger">{this.validationErrors.error}</small>}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group">
                                <label htmlFor="ticketSubject">Oggetto</label>
                                <input type="text"
                                       class={`form-control ${this.validationErrors.ticketSubject && 'invalid-field'}`}
                                       id="ticketSubject" placeholder="Oggetto della segnalazione"
                                       name="ticketSubject" value={this.ticketForm.ticketSubject}
                                       onInput={(event) => this.formValueChanged(event, 'ticketSubject')}/>
                                {this.validationErrors.ticketSubject && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.ticketSubject}</small>}
                            </div>

                            <div class="form-group">
                                <label htmlFor="ticketDescription">Descrizione</label>
                                <textarea
                                    class={`form-control ${this.validationErrors.ticketDescription && 'invalid-field'}`}
                                    id="ticketDescription" placeholder="Descrizione dettagliata..."
                                    name="ticketDescription" value={this.ticketForm.ticketDescription} rows={10}
                                    onInput={(event) => this.formValueChanged(event, 'ticketDescription')}/>
                                {this.validationErrors.ticketDescription && this.showErrors &&
                                    <small
                                        class="form-text text-danger">{this.validationErrors.ticketDescription}</small>}
                            </div>

                            <div class="form-group">
                                <label htmlFor="ticketPriority">Priorità</label>
                                <select id="ticketPriority" class="form-control" name="ticketPriority"
                                        onChange={(event) => this.formValueChanged(event, 'ticketPriority')}>
                                    <option value='0' selected={this.ticketForm.error === '0'}>Media</option>
                                    <option value='1' selected={this.ticketForm.error === '1'}>Alta</option>
                                </select>
                                {this.validationErrors.ticketPriority && this.showErrors &&
                                    <small class="form-text text-danger">{this.validationErrors.ticketPriority}</small>}
                            </div>

                            <div class="form-group">
                                <label>Allega un file</label>
                                <div class="custom-file">
                                    <input type="file" class="custom-file-input" onInput={(e) => this.fileChanged(e)}
                                           id="allegato"
                                           name="allegato"/>
                                    <label class="custom-file-label">{
                                        this.ticketForm.attachedFileName == '' ?
                                            <span>Scegli il file...</span> :
                                            this.ticketForm.attachedFileName}</label>
                                </div>
                            </div>

                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="privacy" name="privacy" value="1"
                                       checked={this.ticketForm.privacyAccepted}
                                       onChange={(event) => this.formValueChanged(event, 'privacyAccepted')}/>
                                <label class="form-check-label">Il sottoscritto dichiara di aver letto preventivamente
                                    l'informativa e
                                    acconsente ai sensi e per
                                    gli effetti dell'art. 13 del Regolamento Europeo 679/2016 (GDPR) al trattamento dei
                                    dati personali
                                    secondo le
                                    modalità e nei limiti di cui <a
                                        href="http://www.starch.it/Portals/0/File/InformativaPrivacyStarch.pdf?ver=2019-02-15-172110-120"
                                        target="_blank">all’informativa allegata</a>.</label>
                            </div>
                            {this.validationErrors.privacyAccepted && this.showErrors &&
                                <small class="form-text text-danger">{this.validationErrors.privacyAccepted}</small>}
                            <div class="mt-3 text-right">
                                <button class="btn btn-primary" type="submit" disabled={this.loading}>Invia
                                    segnalazione
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            )
        }
        if (this.isTicketSent && this.showMessagePages) {
            return (
                <div class="text-center h-100">
                    <div class="mt-5">
                        <img src="http://www.starch.it/Portals/0/Logo_STARCH_2016_O.png?ver=2018-10-30-215215-000"
                             width="200"
                             height="42"
                             alt="" class="mb-5"/>
                        <h2 class="mb-2">Grazie per averci inviato la tua segnalazione.</h2>
                        <p>
                            La sua segnalazione è stata correttamente inviata ed acquisita.<br/>
                            A breve riceverà una conferma di presa in carico da parte di un operatore del centro
                            assistenza di Starch.<br/>
                            I tempi medi di presa in carico variano da qualche minuto ad un paio d'ore. Vi chiediamo
                            gentilmente di attendere.<br/>
                            Vi ringraziamo per la collaborazione.
                        </p>
                    </div>
                </div>
            )
        }
        if (this.isTicketError && this.showMessagePages) {
            return (
                <div class="text-center h-100">
                    <div class="mt-5">
                        <img src="http://www.starch.it/Portals/0/Logo_STARCH_2016_O.png?ver=2018-10-30-215215-000"
                             width="200"
                             height="42"
                             alt="" class="mb-5"/>
                        <h2 class="mb-2">Uh oh... Qualcosa è andato storto.</h2>
                        <h4><a onClick={() => this.isTicketError = false}>Riprova ad inserire la tua segnalazione</a>, o
                            contattaci
                            direttamente al numero <b>039.62.860</b>.</h4>
                    </div>
                </div>
            );
        }
    }

    isCPortal360() {
        return this.application === 'Cportal 360';
    }

    isArchiWeb() {
        return this.application === 'ArchiWeb';
    }

    cportalZohoEnabled(): boolean {
        return this.isCPortal360();
    }

    archiwebZohoEnabled(): boolean {
        return this.isArchiWeb();
    }

    render() {

        if (this.cportalZohoEnabled()) {
            return (
                <div class="jumbotron">
                    <h1>Richiesta di assistenza tecnica</h1>
                    <p>Se stai riscontrando problemi tecnici durante la presentazione dell'istanza, come errori
                        dell'applicazione o difficoltà di visualizzazione, ti invitiamo a richiedere assistenza tecnica
                        attraverso il nostro portale dedicato.<br />
                        Puoi accedervi al seguente indirizzo: <a
                            href='https://supporto.cportal.it' target='_blank'>https://supporto.cportal.it</a>.<br />
                        I nostri tecnici si occuperanno prontamente della tua segnalazione.<br />
                        Ti ringraziamo per la collaborazione.</p>
                    <p>
                        <a class="btn btn-primary" href="https://supporto.cportal.it" target="_blank">Accedi al portale di assistenza</a>
                    </p>
                </div>
            )
        }

        if (this.archiwebZohoEnabled()) {

            if (this.codCat === 'G337') {
                return (
                    <div class="jumbotron" style={{fontSize: '14px'}}>
                        <h3 style={{fontSize: '24px', textTransform: 'none'}}>Novità per gli utenti di ArchiWeb!</h3>
                        <p style={{fontSize: '14px'}}>
                            Per l’assistenza rivolgersi all’Help Desk di It.City S.p.A. utilizzando il portale di assistenza all’indirizzo&nbsp;
                            <a href="https://helpdesk.comune.parma.it" style={{display: 'inline'}} target="_blank">https://helpdesk.comune.parma.it</a> inviando
                            una mail a&nbsp;
                            <a href="mailto:sos@itcity.it" style={{display: 'inline'}} target="_blank">sos@itcity.it</a><br />o chiamando il numero di telefono breve
                            7333.
                        </p>
                    </div>
                )
            }

            return (
                <div class="jumbotron" style={{fontSize: '14px'}}>
                    <h3 style={{fontSize: '24px', textTransform: 'none'}}>Novità per gli utenti di ArchiWeb!</h3>
                    <p style={{fontSize: '14px'}}>
                        Accedete ora a <a href='https://supporto.archiweb.cloud'
                                          style={{display: 'inline-block', fontSize: '14px'}}
                                          target='_blank'>https://supporto.archiweb.cloud</a> per beneficiare dei
                        seguenti
                        vantaggi:
                    </p>
                    <ul style={{fontSize: '14px'}}>
                        <li>
                            Apertura dei ticket per assistenza tecnica.
                        </li>
                        <li>
                            Monitoraggio dell'avanzamento dei ticket in tempo reale.
                        </li>
                        <li>
                            Accesso allo storico completo dei ticket inoltrati.
                        </li>
                    </ul>

                    <p style={{fontSize: '14px'}}>La registrazione al portale è consigliata per massimizzare
                        l'efficienza e la gestione delle
                        vostre richieste di supporto.
                        Tuttavia, è possibile aprire nuovi ticket anche senza registrazione.</p>

                    <p style={{fontSize: '14px'}}>Grazie per la vostra fiducia e supporto continuo.</p>
                </div>
            )
        }


        switch (this.layout) {
            case 'starch':
                return this.starchLayout();
            case 'standard':
            default:
                return this.standardLayout();
        }
    }
}
